import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isSpatialLayerDynamic } from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import { gspx } from '@rsos/gainsight';
import { toggleSpatialLayer } from '../../actions/spatialLayers';
import { trackAirAmbulanceLayerToggle } from '../../gainsightTrackings/airAmbulanceTrackings';
import useGISHelpers from '../../hooks/useGISHelpers';
import { trackToggleSupplementalLayer } from '../JurisdictionView/Map/gainsight/gisTrackings';
import BasemapLayerCheckbox from './BasemapLayerCheckbox';
import {
  LayerCheckboxProps,
  Sublayer,
  RootState,
  isSpatialLayer,
  isGISLayer,
} from './LayerCheckbox.types';
import NormalizedLayerCheckbox from './NormalizedLayerCheckbox';
import SupplementalLayerCheckbox from './SupplementalLayerCheckbox';

const STATIC_LAYERS_CARD = 'staticLayersCard';

declare const closeCard: (cardName: string) => {
  type: string;
  payload: string;
};
declare const setMapCardData: (data: unknown) => {
  type: string;
  payload: unknown;
};

const AIR_AMBULANCE_HELICOPTER_DYNAMIC_LAYER = 'gmr_air';

/**
 * Component for handling the checkbox of a spatial layer
 */
const LayerCheckbox: React.FC<LayerCheckboxProps> = ({
  layer,
  isDisabled,
  isGISSupplementalLayer,
}) => {
  const dispatch = useDispatch();
  const { activeGISServices, toggleGISService } = useGISHelpers();
  const isLayerDynamic = isSpatialLayerDynamic(layer);
  const layerID = layer.id;
  const layerKey = isSpatialLayer(layer) ? layer.key : `${layer.id}`;
  const isBasemapLayer = layer.service_type === 0;
  const layerData = useSelector(
    (state: RootState) => state.mapCard?.mapCardData?.layerData,
  );
  const { irpVersion } = useSelector((state: RootState) => state.sinatra.irp);

  const handleCheckboxOnChange = useCallback(() => {
    if (!isSpatialLayer(layer)) return;

    // layer.isChecked is in previous state here before reset
    const onOffText = layer.isChecked ? 'off' : 'on';
    const { name, source } = layer;

    if (!isLayerDynamic && source === layerData?.source) {
      dispatch(closeCard(STATIC_LAYERS_CARD));
      dispatch(setMapCardData(null));
    } else {
      //TODO: add handling for dynamic layers
    }

    gspx.trackCustomEvent('Data Map', {
      irpVersion,
      name: `Turn ${onOffText} ${name}`,
      Category: 'Spatial Layer',
      'Launched date': new Date(),
    });

    if (layerKey === AIR_AMBULANCE_HELICOPTER_DYNAMIC_LAYER) {
      trackAirAmbulanceLayerToggle({
        layerId: AIR_AMBULANCE_HELICOPTER_DYNAMIC_LAYER,
        isVisible: !layer.isChecked,
      });
    }

    dispatch(toggleSpatialLayer(layerKey, isLayerDynamic, !layer.isChecked));
  }, [
    dispatch,
    layer,
    layerKey,
    isLayerDynamic,
    layerData?.source,
    irpVersion,
  ]);

  // handles the parent layer checkbox
  const handleGISCheckboxOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isGISLayer(layer)) return;

      const onOffText = event.target.checked ? 'on' : 'off';
      const layerDisplayName = layer.display_name;

      gspx.trackCustomEvent('Data Map', {
        irpVersion,
        name: `Turn ${onOffText} ${layerDisplayName}`,
        Category: 'GIS Service',
        'Launched date': new Date(),
      });

      // Track supplemental layer toggle
      trackToggleSupplementalLayer({
        layerName: layerDisplayName,
        isVisible: event.target.checked,
        layerCategory: (layer as any).category?.name || 'gis_service',
      });

      dispatch(toggleGISService({ layer, isSublayer: false }));
    },
    [dispatch, layer, irpVersion, toggleGISService],
  );

  // handles the sublayer checkbox
  const handleSublayerCheckboxOnChange = useCallback(
    (sublayer: Sublayer) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const onOffText = event.target.checked ? 'on' : 'off';
      const sublayerDisplayName = sublayer.display_name || sublayer.title;

      gspx.trackCustomEvent('Data Map', {
        irpVersion,
        name: `Turn ${onOffText} ${sublayerDisplayName}`,
        Category: 'GIS Sublayer',
        'Launched date': new Date(),
      });

      if (!isBasemapLayer) {
        // Parent is a supplemental layer
        trackToggleSupplementalLayer({
          layerName: sublayerDisplayName || 'unknown_sublayer',
          isVisible: event.target.checked,
          layerCategory: (layer as any).category?.name || 'gis_sublayer',
        });
      }

      dispatch(toggleGISService({ layer: sublayer, isSublayer: true }));
    },
    [dispatch, irpVersion, toggleGISService, isBasemapLayer, layer],
  );

  // Check if this is a GIS layer with multiple sublayers (accordion case)
  return isGISSupplementalLayer &&
    isGISLayer(layer) &&
    layer.sublayers &&
    layer.sublayers.length > 1 ? (
    <SupplementalLayerCheckbox
      layer={layer}
      layerID={layerID}
      isDisabled={isDisabled}
      activeGISServices={activeGISServices}
      handleGISCheckboxOnChange={handleGISCheckboxOnChange}
      handleSublayerCheckboxOnChange={handleSublayerCheckboxOnChange}
    />
  ) : isBasemapLayer &&
    isGISLayer(layer) &&
    layer.sublayers &&
    layer.sublayers.length > 1 ? (
    // Basemap layer with sublayers
    <BasemapLayerCheckbox
      layer={layer}
      layerID={layerID}
      isDisabled={isDisabled}
      activeGISServices={activeGISServices}
      handleGISCheckboxOnChange={handleGISCheckboxOnChange}
      handleSublayerCheckboxOnChange={handleSublayerCheckboxOnChange}
    />
  ) : (
    // Regular single checkbox for layers without sublayers or non-GIS layers
    <NormalizedLayerCheckbox
      layer={layer}
      layerID={layerID}
      isDisabled={isDisabled}
      isGISSupplementalLayer={isGISSupplementalLayer}
      activeGISServices={activeGISServices}
      handleCheckboxOnChange={handleCheckboxOnChange}
      handleGISCheckboxOnChange={handleGISCheckboxOnChange}
    />
  );
};

export default LayerCheckbox;
